import { useCallback, useMemo } from 'react';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { PaywallLocation, PurchaseResultType, useBillingPopup } from '@blocs.paywall';
import { useRouter } from '@blocs.router';
import { canScheduleHeadshotsAfterUpgrade } from '@consumer-talent.utils/isPaidTrialSubscriber';
import routeHelper from 'src/routes/routeHelper';

export type UseHeadshotFreeModalParams = {
  /** Closes the open headshots modal; owned by `useHeadshotModal`. */
  onClose: () => void;
};

/**
 * Free and Essential headshots upsell: the upgrade callback.
 * `useHeadshotModal` owns which modal is open.
 */
export const useHeadshotFreeModal = ({ onClose }: UseHeadshotFreeModalParams) => {
  const billingPopup = useBillingPopup();
  const { navigate } = useRouter();

  const handleFreeUpgrade = useCallback(async () => {
    captureHeapEvent({
      name: HEAP_EVENT_NAMES.TALENT_CLICK_HEADSHOTS_FREE_UPGRADE_MODULE_UPGRADE_NOW,
    });
    onClose();

    const { getPurchaseResult } = billingPopup.show({
      location: PaywallLocation.HEADSHOTS_FREE,
    });

    // A paused subscription opens the resume modal and does not return a purchase promise.
    if (!getPurchaseResult) {
      return;
    }

    // This await lives on the nav, which stays mounted after the free modal closes.
    // BillingPopupProvider resolves the promise when the paywall closes. Leaving the
    // page unmounts that provider first, and the deferred promise rejects with
    // "Component unmounted". That is cancellation, not a failed purchase.
    let purchaseResult;
    try {
      purchaseResult = await getPurchaseResult();
    } catch (error) {
      if (error === 'Component unmounted') {
        return;
      }

      throw error;
    }

    const purchasedSubscription = purchaseResult.completedSubscriptionPurchase?.subscription;

    if (
      purchaseResult.type === PurchaseResultType.Success &&
      canScheduleHeadshotsAfterUpgrade({
        trialEndsAt: purchasedSubscription?.trialEndsAt,
        plan: purchasedSubscription?.plan,
      })
    ) {
      navigate({ to: routeHelper.headshots.link() });
    }
  }, [billingPopup, navigate, onClose]);

  return useMemo(
    () => ({
      handleFreeUpgrade,
    }),
    [handleFreeUpgrade]
  );
};

export type UseHeadshotFreeModalValues = ReturnType<typeof useHeadshotFreeModal>;
